import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { AlertCircle, AlertTriangle, Check, CheckCircle, Clock, Edit2, Info, Send, Slash, X, XCircle } from "react-feather";
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import styles from "./vehicledetail.module.css";
import PhotoCarousel from "./PhotoCarousel";
import RejectVehicleDialog from "./RejectVehicleDialog";
// Parking passes / payment links are no longer used for guest access
// import { guestPassDisplay, useSendAccessLink, useSendPaymentLink } from "./guestPass";
import { useSendAccessLink } from "./guestPass";
import { formatPhone, getApproval, getRequestId, normalizePhotos, rowPhotos } from "./vehicleRows";
import { apiErrorMessage, formatDate, isGuestType, isUpdateType } from "./vehicleRequestUtils";

const COMPARE_FIELDS = [
  { key: "license", label: "License plate" },
  { key: "make", label: "Make" },
  { key: "model", label: "Model" },
  { key: "color", label: "Colour" },
];
// Guest updates can also change the guest's name and phone (guest edit link)
const GUEST_COMPARE_FIELDS = [
  { key: "guest_name", label: "Guest name", value: (item) => [item?.guest_fname, item?.guest_lname].filter(Boolean).join(" ") },
  { key: "guest_phone", label: "Phone", value: (item) => formatPhone(item?.guest_phone || "") },
];

/* Parking passes are no longer used for guest access
// Pass badge colors / status icon per guestPassDisplay tone
const PASS_TONES = {
  active: { color: "#047857", background: "#D1FAE5", icon: <CheckCircle size={14} /> },
  awaiting: { color: "#B45309", background: "#FEF3C7", icon: <Clock size={14} /> },
  expired: { color: "#B91C1C", background: "#FEE2E2", icon: <X size={14} /> },
  none: { color: "#6B7280", background: "#F3F4F6", icon: <Clock size={14} /> },
};
*/

const daysText = (days) => (days !== null && days !== undefined ? ` · ${days} day${Number(days) === 1 ? "" : "s"}` : "");

const TopCol = ({ label, children }) => (<div className={styles.TopCol}>
  <span className={styles.Label}>{label}</span>
  <span className={styles.Value}>{children}</span>
</div>);

const DetailItem = ({ label, children, full }) => (<div className={full ? `${styles.DetailItem} ${styles.DetailFull}` : styles.DetailItem}>
  <span className={styles.Label}>{label}</span>
  <span className={styles.DetailValue}>{children}</span>
</div>);

/*
 * Details of one vehicle row (list expanded row, or a dialog from the grid / mobile cards).
 * Pending: review the request (approve / reject). Approved: edit, disable / enable, guest unlock link. Rejected: reason.
 * onChanged() runs after any change so the parent reloads the list.
 */
const VehicleDetailPanel = ({ row, canUpdate, onChanged, onEdit, onDelete, onEditRequest, onDeleteRequest, notifySuccess }) => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  // const [sendingId, sendPaymentLink] = useSendPaymentLink(onChanged, notifySuccess);
  const [sendingAccessId, sendAccessLink] = useSendAccessLink(notifySuccess);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [detailsReload, setDetailsReload] = useState(0);
  const [approving, setApproving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [toggleOpen, setToggleOpen] = useState(false);
  const [toggling, setToggling] = useState(false);

  const permissions = (isAuthenticated && user?.["https://propertydek.com/permissions"]) || [];
  const canSeeTenant = permissions.indexOf("tenant-contactinfo:read") > -1 && permissions.indexOf("tenant-journey:read") > -1;

  const approval = getApproval(row);
  const isGuest = row?.is_guest === 1;
  const isVehicle = row?.kind !== "request";
  const requestId = getRequestId(row);
  const mode = approval.state === "pending" || row?.pending_update ? "pending" : approval.state === "rejected" ? "rejected" : "vehicle";

  // The request (photos, requested values, current vehicle) is loaded only when the panel opens
  useEffect(() => {
    if (mode === "vehicle" || !requestId) return undefined;
    let cancelled = false;

    async function loadDetails() {
      setDetailsLoading(true);
      setDetailsError("");
      try {
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
        const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/vehicle-requests/" + requestId, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setDetails(response?.data?.data || null);
      } catch (error) {
        if (cancelled) return;
        console.error("[Vehicles] Failed to load vehicle request", error);
        setDetailsError(apiErrorMessage(error, "Unable to load the submitted details."));
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    }
    loadDetails();
    return () => { cancelled = true; };
  }, [mode, requestId, detailsReload, getAccessTokenSilently]);

  const requestType = details?.type || row?.pending_update?.type || approval.type;
  const isUpdate = Boolean(row?.pending_update) || isUpdateType(requestType);
  const guestVehicle = isGuest || isGuestType(requestType);

  // Requested values for a request; the row itself is the current vehicle
  const current = details?.current_vehicle || (isVehicle ? row : null);
  const shown = mode === "vehicle" ? row : {
    license: details?.license ?? row?.license,
    make: details?.make ?? row?.make,
    model: details?.model ?? row?.model,
    color: details?.color ?? row?.color,
  };

  const requestPhotos = normalizePhotos(details?.photos);
  let photos = rowPhotos(row);
  if (mode !== "vehicle" && requestPhotos.length > 0) photos = requestPhotos;

  const tenantName = guestVehicle
    ? row?.tenant_name || [details?.tenant?.first_name, details?.tenant?.last_name].filter(Boolean).join(" ")
    : row?.name || [details?.tenant?.first_name, details?.tenant?.last_name].filter(Boolean).join(" ");
  const guestName = row?.name || [details?.guest_fname, details?.guest_lname].filter(Boolean).join(" ");
  const phone = row?.phone || formatPhone(guestVehicle ? details?.guest_phone : details?.tenant?.phone) || "-";
  const firstName = guestVehicle
    ? row?.guest_fname || details?.guest_fname || "the guest"
    : details?.tenant?.first_name || (row?.name || "").split(" ")[0] || "the tenant";
  const others = row?.other_vehicles_in_unit || [];
  const propertyUnit = [row?.property || details?.property_name, row?.unit || details?.apt_no].filter(Boolean).join(" · ") || "-";

  const approve = async () => {
    if (!requestId) return;
    setApproving(true);
    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      const response = await axios.post(process.env.REACT_APP_TENANT_API_URL + "v1/vehicle-requests/" + requestId + "/approve", {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (notifySuccess) notifySuccess(response?.data?.message || "Vehicle approved.");
      if (onChanged) onChanged();
    } catch (error) {
      console.error("[Vehicles] Failed to approve vehicle request", error);
      toast.error(apiErrorMessage(error, "Unable to approve the vehicle. Please try again."));
    } finally {
      setApproving(false);
    }
  };

  const toggleDisabled = async () => {
    const vehicleId = row?.vehicle_id || row?.id;
    const action = row?.disabled ? "enable" : "disable";
    setToggling(true);
    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      const response = await axios.post(process.env.REACT_APP_TENANT_API_URL + "v1/vehicles/" + vehicleId + "/" + action, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (notifySuccess) notifySuccess(response?.data?.message || (action === "enable" ? "Vehicle enabled." : "Vehicle disabled."));
      setToggleOpen(false);
      if (onChanged) onChanged();
    } catch (error) {
      console.error(`[Vehicles] Failed to ${action} vehicle`, error);
      toast.error(apiErrorMessage(error, `Unable to ${action} the vehicle. Please try again.`));
    } finally {
      setToggling(false);
    }
  };

  const tenantCell = tenantName
    ? (row?.tenant_id && canSeeTenant ? <Link to={"/people/tenants/details/" + row.tenant_id} className={styles.TenantLink}>{tenantName}</Link> : tenantName)
    : "-";

  const detailsGrid = (<div className={styles.DetailGrid}>
    <DetailItem label="LICENSE PLATE"><span className={styles.Plate}>{shown?.license || "-"}</span></DetailItem>
    <DetailItem label="MAKE / MODEL">{[shown?.make, shown?.model].filter(Boolean).join(" ") || "-"}</DetailItem>
    <DetailItem label="COLOUR">{shown?.color || "-"}</DetailItem>
    <DetailItem label="TYPE">{guestVehicle ? "Guest vehicle" : "Tenant vehicle"}</DetailItem>
    {guestVehicle && <DetailItem label="GUEST">{guestName || "-"}</DetailItem>}
    <DetailItem label={guestVehicle ? "GUEST OF" : "TENANT"}>{tenantCell}</DetailItem>
    <DetailItem label="PROPERTY / UNIT">{propertyUnit}</DetailItem>
    <DetailItem label="PHONE">{phone}</DetailItem>
    <DetailItem label="OTHER VEHICLES IN UNIT" full>
      {others.length > 0 ? (<span className={styles.PlateList}>{others.map((plate, i) => <span key={plate + i} className={styles.PlateSmall}>{plate}</span>)}</span>) : "None"}
    </DetailItem>
  </div>);

  const compareTable = isUpdate && details && (<div className={styles.CompareWrap}>
    <p className={styles.SectionTitle}>Requested changes</p>
    <table className={styles.CompareTable}>
      <thead>
        <tr><th></th><th>Current</th><th>Requested</th></tr>
      </thead>
      <tbody>
        {(guestVehicle ? COMPARE_FIELDS.concat(GUEST_COMPARE_FIELDS) : COMPARE_FIELDS).map((field) => {
          const before = (field.value ? field.value(current) : current?.[field.key]) || "";
          const after = (field.value ? field.value(details) : details?.[field.key]) || "";
          const changed = before !== after;
          return (<tr key={field.key}>
            <td className={styles.CompareLabel}>{field.label}</td>
            <td>{before || "-"}</td>
            <td className={changed ? styles.ChangedValue : ""}>{after || "-"}</td>
          </tr>);
        })}
      </tbody>
    </table>
    {!details?.current_vehicle && !isVehicle && (<p className={styles.MutedText}>The vehicle being updated no longer exists.</p>)}
  </div>);

  const body = (<div className={styles.Body}>
    <div className={styles.BodyPhotos}>
      {detailsLoading && mode !== "vehicle" ? (<div className={styles.LoadingBox}><CircularProgress size={24} /></div>) : (<PhotoCarousel photos={photos} fromTenant={mode === "pending" && !guestVehicle} />)}
    </div>
    <div className={styles.BodyDetails}>
      {detailsGrid}
      {compareTable}
      {detailsError !== "" && (<p className={styles.ErrorText}>
        {detailsError} <button type="button" className={styles.LinkButton} onClick={() => setDetailsReload((n) => n + 1)}>Try again</button>
      </p>)}
    </div>
  </div>);

  const history = row?.history ? <p className={styles.History}>{row.history}</p> : null;

  // ── Pending approval (new vehicle, or changes to an approved one) ──
  if (mode === "pending") {
    const days = row?.pending_update ? row.pending_update.days : approval.days;
    const submittedAt = details?.submitted_at || row?.pending_update?.submitted_at || approval.at;

    return (<div className={styles.Panel} onClick={(e) => e.stopPropagation()}>
      <div className={styles.TopRow}>
        <TopCol label="SUBMITTED">{[submittedAt ? formatDate(submittedAt) : "", approval.source].filter(Boolean).join(" · ") || "-"}</TopCol>
        <TopCol label="STATUS">
          <span className={styles.StatusPending}><Clock size={14} /> {row?.pending_update ? "Update pending" : "Pending approval"}{daysText(days)}</span>
        </TopCol>
        {canUpdate && (<div className={styles.Actions}>
          <button type="button" className={styles.BtnPrimary} disabled={approving || !requestId} onClick={approve}>
            <Check size={14} /> {approving ? "Approving..." : row?.pending_update ? "Approve changes" : "Approve vehicle"}
          </button>
          <button type="button" className={styles.BtnDangerOutline} disabled={approving || !requestId} onClick={() => setRejectOpen(true)}>Reject</button>
        </div>)}
      </div>

      {body}

      <div className={`${styles.Notice} ${styles.NoticeAmber}`}>
        <AlertTriangle size={16} />
        <span>
          {row?.pending_update
            ? "The current details stay in use until these changes are approved."
            : "Parking and gate access is blocked until this vehicle is approved."}
          {" "}Approving notifies {firstName} {guestVehicle ? "by SMS." : "in the tenant app."}
        </span>
      </div>
      {history}

      <RejectVehicleDialog
        open={rejectOpen}
        requestId={requestId}
        plate={shown?.license}
        name={guestVehicle ? guestName : tenantName}
        property={row?.property || details?.property_name}
        unit={row?.unit || details?.apt_no}
        onClose={() => setRejectOpen(false)}
        notifySuccess={notifySuccess}
        onRejected={() => { setRejectOpen(false); if (onChanged) onChanged(); }}
      />
    </div>);
  }

  // ── Rejected request ──
  if (mode === "rejected") {
    const reason = approval.reviewNote || details?.review_note || "";
    const rejectedBy = approval.by || details?.reviewed_by_name || "";
    const rejectedAt = approval.at || details?.reviewed_at;

    return (<div className={styles.Panel} onClick={(e) => e.stopPropagation()}>
      <div className={styles.TopRow}>
        <TopCol label="STATUS"><span className={styles.StatusRejected}><XCircle size={14} /> Rejected</span></TopCol>
        <TopCol label="REJECTED BY">{rejectedBy || "-"}</TopCol>
        <TopCol label="REJECTED ON">{rejectedAt ? formatDate(rejectedAt) : "-"}</TopCol>
        {details?.submitted_at && <TopCol label="SUBMITTED">{[formatDate(details.submitted_at), approval.source].filter(Boolean).join(" · ")}</TopCol>}
        {/* Rejected new vehicle / guest: staff can correct it (stays rejected) or remove it from the list */}
        {canUpdate && !isVehicle && (onEditRequest || onDeleteRequest) && (<div className={styles.Actions}>
          {onEditRequest && (<button type="button" className={styles.BtnOutline} onClick={() => onEditRequest(row)}><Edit2 size={14} /> Edit vehicle</button>)}
          {onDeleteRequest && (<button type="button" className={styles.BtnDangerOutline} onClick={() => onDeleteRequest(row)}>Delete</button>)}
        </div>)}
      </div>

      <div className={`${styles.Notice} ${styles.NoticeRed}`}>
        <AlertCircle size={16} />
        <span><strong>Reason:</strong> {reason || "No reason given."} The {guestVehicle ? "tenant and guest" : "tenant"} can edit and resubmit.</span>
      </div>

      {body}
      {history}
    </div>);
  }

  // ── Approved vehicle (active or disabled) ──
  const disabled = Boolean(row?.disabled) || approval.state === "disabled";
  const gate = row?.gate_access || (disabled ? "blocked" : "allowed");
  const automatic = approval.source === "Staff" || /auto/i.test(approval.sub);
  // const pass = isGuest ? (row?.pass || guestPassDisplay(row?.guest_pass)) : null;
  // const tone = pass ? (PASS_TONES[pass.tone] || PASS_TONES.none) : null;
  const vehicleId = row?.vehicle_id || row?.id;

  return (<div className={styles.Panel} onClick={(e) => e.stopPropagation()}>
    <div className={styles.TopRow}>
      <TopCol label="APPROVAL">
        {disabled
          ? (<span className={styles.StatusDisabled}><Slash size={14} /> Disabled</span>)
          : (<span className={styles.StatusApproved}><Check size={14} /> {automatic ? "Approved - automatic" : "Approved"}</span>)}
      </TopCol>
      <TopCol label="APPROVED ON">{approval.at || row?.created_at ? formatDate(approval.at || row?.created_at) : "-"}</TopCol>
      <TopCol label="ADDED BY">{approval.by || approval.source || "-"}</TopCol>
      {/* <TopCol label="PARKING PASS">
        {pass ? (<span className={styles.PassBadge} style={{ color: tone.color, background: tone.background }}>{pass.badge}</span>) : "Not needed"}
      </TopCol> */}
      <TopCol label="GATE ACCESS">
        <span className={gate === "blocked" ? styles.GateBlocked : styles.GateAllowed}>{gate === "blocked" ? "Blocked" : "Allowed"}</span>
      </TopCol>
      {canUpdate && isVehicle && (<div className={styles.Actions}>
        {onEdit && (<button type="button" className={styles.BtnOutline} onClick={() => onEdit(row)}><Edit2 size={14} /> Edit vehicle</button>)}
        <button type="button" className={disabled ? styles.BtnOutline : styles.BtnDangerOutline} onClick={() => setToggleOpen(true)}>
          {disabled ? "Enable vehicle" : "Disable vehicle"}
        </button>
      </div>)}
    </div>

    {body}

    {/* Guest unlock link: texted to the guest's phone; the guest verifies with a one-time code. Not tied to any payment. */}
    {isGuest && isVehicle && !disabled && (<div className={styles.PassSection}>
      <p className={styles.SectionTitle}>Guest access</p>
      <div className={styles.PassRow}>
        <TopCol label="UNLOCK LINK SENT TO">{row?.phone || formatPhone(row?.guest_phone || "") || "-"}</TopCol>
        {canUpdate && (<div className={styles.Actions}>
          <button type="button" className={styles.BtnPrimary} disabled={sendingAccessId === vehicleId} onClick={() => sendAccessLink(vehicleId)}>
            <Send size={14} /> {sendingAccessId === vehicleId ? "Sending..." : "Send unlock link (SMS)"}
          </button>
        </div>)}
      </div>
      <div className={`${styles.Notice} ${styles.NoticeAmber}`}>
        <Info size={16} />
        <span>Each guest has their own permanent link. Sending again texts the same link. Disabling or deleting the vehicle blocks it right away.</span>
      </div>
    </div>)}

    {/* Parking pass section: payments / passes are no longer used for guest access
    {pass && (<div className={styles.PassSection}>
      <p className={styles.SectionTitle}>Parking pass</p>
      <div className={styles.PassRow}>
        <TopCol label="PASS TYPE">{pass.passName}{pass.price !== null ? ` · $${pass.price.toFixed(2)}` : ""}</TopCol>
        <TopCol label="STATUS"><span className={styles.PassStatus} style={{ color: tone.color }}>{tone.icon} {pass.statusText}</span></TopCol>
        {pass.validFrom && <TopCol label="VALID FROM">{pass.validFrom}</TopCol>}
        {pass.wasValid && <TopCol label="WAS VALID">{pass.wasValid}</TopCol>}
        {pass.expires && <TopCol label="EXPIRES">{pass.expires}</TopCol>}
        {pass.remaining && <TopCol label="REMAINING">{pass.remaining}</TopCol>}
        {canUpdate && isVehicle && (<div className={styles.Actions}>
          {pass.canSend && (<button type="button" className={styles.BtnPrimary} disabled={sendingId === (row.vehicle_id || row.id)} onClick={() => sendPaymentLink(row.vehicle_id || row.id)}>
            <Send size={14} /> {sendingId === (row.vehicle_id || row.id) ? "Sending..." : pass.sendLabel}
          </button>)}
          {pass.canSendAccess && (<button type="button" className={styles.BtnPrimary} disabled={sendingAccessId === (row.vehicle_id || row.id)} onClick={() => sendAccessLink(row.vehicle_id || row.id)}>
            <Send size={14} /> {sendingAccessId === (row.vehicle_id || row.id) ? "Sending..." : "Send access link"}
          </button>)}
          {onDelete && (<button type="button" className={styles.BtnDangerOutline} onClick={() => onDelete(row)}>{pass.revokeLabel}</button>)}
        </div>)}
      </div>
      {pass.alertText && (<div className={`${styles.Notice} ${pass.alertTone === "error" ? styles.NoticeRed : styles.NoticeAmber}`}>
        {pass.alertTone === "error" ? <AlertCircle size={16} /> : <AlertTriangle size={16} />}
        <span>{pass.alertText}</span>
      </div>)}
    </div>)} */}

    {disabled ? (<div className={`${styles.Notice} ${styles.NoticeGrey}`}>
      <Info size={16} />
      <span>This vehicle is disabled. Parking and gate access is off until it is enabled again.</span>
    </div>) : (<div className={`${styles.Notice} ${styles.NoticeGreen}`}>
      <CheckCircle size={16} />
      <span>Parking and gate access is on. Disabling the vehicle removes access right away and notifies the tenant.</span>
    </div>)}
    {history}

    <Dialog open={toggleOpen} onClose={toggling ? undefined : () => setToggleOpen(false)} fullWidth maxWidth="sm" className="NewPopUp">
      <DialogTitle>
        {disabled ? `Enable vehicle ${row?.license || ""}?` : `Disable vehicle ${row?.license || ""}?`}
        <button type="button" onClick={() => setToggleOpen(false)} disabled={toggling}><X /></button>
      </DialogTitle>
      <DialogContent>
        <div className="ModalFormGroup">
          <label className="PopupBodyText">
            {disabled
              ? "Parking and gate access will be turned back on and the tenant will be notified."
              : "Parking and gate access will be removed right away and the tenant will be notified."}
          </label>
        </div>
      </DialogContent>
      <DialogActions>
        <button type="button" className={styles.BtnGhost} disabled={toggling} onClick={() => setToggleOpen(false)}>Cancel</button>
        <button type="button" className={disabled ? styles.BtnPrimary : styles.BtnDanger} disabled={toggling} onClick={toggleDisabled}>
          {toggling ? "Please wait..." : disabled ? "Enable vehicle" : "Disable vehicle"}
        </button>
      </DialogActions>
    </Dialog>
  </div>);
};

export default VehicleDetailPanel;
